import { useCallback, useContext, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { t } from 'i18next'

import { FeedbackContext } from '~/components/feedback'
import ToggleInput from '~/components/Toggle'
import Tooltip from '~/components/Tooltip'

import { useCampaignReadOnly } from '../../hooks/useCampaignReadOnly'
import { toggleProductStatusService } from '../../services/campaign/product'

const ToggleActiveCampaignProduct = ({
  data,
  showLabel = false,
  deactivatedMessage
}: {
  data: CampaignProduct
  showLabel?: boolean
  deactivatedMessage?: string
}) => {
  const defaultMsg = t('rm:ad.deactivatedProduct.description')
  const deactivatedMsg = deactivatedMessage || defaultMsg
  const { id } = useParams()
  const queryClient = useQueryClient()
  const { toast } = useContext(FeedbackContext)

  const [active, setActive] = useState<boolean>(data?.active)

  const label = useMemo(
    () => (active ? t('rm:ad.activeProduct') : t('rm:ad.deactivatedProduct')),
    [active]
  )

  const isChecked = useMemo(() => active, [active])

  // Mesma escolha do toggle de anúncio: visível e desabilitado, porque ele é o
  // único lugar da tabela que mostra se o produto está ativo.
  const isReadOnlyView = useCampaignReadOnly()

  const { mutate, isPending } = useMutation({
    mutationFn: async (value: boolean) => {
      const payload = {
        productId: data.id,
        body: {
          active: value,
          campaign_id: id
        }
      }
      return toggleProductStatusService(payload)
    },
    onSuccess: (_, value) => {
      queryClient.invalidateQueries({
        queryKey: ['list-related-products']
      })

      const successMessage = value
        ? t('rm:ad.activeProduct.description')
        : deactivatedMsg

      toast.success(successMessage)

      setActive(value)
    },
    onError: error => {
      console.error(error)
      toast.critical(t('common:messages.errorOccurred.message'))
      setActive(prev => !prev)
    }
  })

  const handleChange = useCallback(
    (e: { e: { target: { checked: boolean } } }) => {
      const value = e.e.target.checked
      setActive(value)
      mutate(value, {
        onError: () => {
          setActive(prev => !prev)
        }
      })
    },
    [mutate]
  )

  return (
    <Tooltip
      interactiveTrigger
      icon={
        <ToggleInput
          name={`product_${data?.product_sku}`}
          isChecked={isChecked}
          label={showLabel ? label : null}
          onChange={handleChange}
          data-testid="toggleAdverts"
          disabled={isPending || isReadOnlyView}
          size="small"
        />
      }
      text={label}
      positionProp="right"
    />
  )
}

export default ToggleActiveCampaignProduct
